import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/lib/auth";
import MatrixBackground from "@/components/auth/MatrixBackground";
import PasswordInput from "@/components/auth/PasswordInput";
import numirockWordmark from "@/assets/numirock-wordmark.png";

export default function LoginPage() {
  const { login, formatApiError } = useAuth();
  const nav = useNavigate();
  const { state } = useLocation();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      await login(email.trim(), password);
      toast.success("Signed in");
      nav(state?.from || "/desk", { replace: true });
    } catch (e) {
      const msg = formatApiError(e);
      setErr(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="aurion-grain relative flex min-h-screen items-center justify-center px-6">
      <MatrixBackground />
      <div className="relative z-10 w-full max-w-md">
        <div className="mb-10 text-center">
          <img
            src={numirockWordmark}
            alt="NUMIROCK"
            data-testid="brand-wordmark"
            className="mx-auto block h-auto w-full max-w-[300px] select-none"
            draggable={false}
          />
          <div
            data-testid="brand-tagline"
            className="mt-3 text-[10px] font-medium uppercase"
            style={{
              color: "var(--aurion-gold)",
              opacity: 0.6,
              letterSpacing: "0.35em",
              fontFamily:
                '"Cormorant Garamond", "Marcellus", ui-serif, Georgia, serif',
            }}
          >
            Performance · Precision · Legacy
          </div>
          <div className="mt-5 text-[10px] uppercase tracking-widest text-[var(--aurion-muted)]">
            Invite-only trading intelligence
          </div>
        </div>
        <Card className="border-[var(--aurion-border)] bg-[var(--aurion-panel)] p-8">
          <h1 className="mb-6 text-sm uppercase tracking-widest text-[var(--aurion-muted)]">
            Sign in
          </h1>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-widest text-[var(--aurion-muted)]">
                Email
              </Label>
              <Input
                id="email"
                data-testid="login-email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-2 h-11 border-[var(--aurion-border)] bg-[var(--aurion-panel-2)]"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-widest text-[var(--aurion-muted)]">
                Password
              </Label>
              <PasswordInput
                id="password"
                data-testid="login-password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="mt-2 h-11 border-[var(--aurion-border)] bg-[var(--aurion-panel-2)]"
              />
            </div>
            {err ? (
              <div data-testid="login-error" className="text-sm text-red-400">
                {err}
              </div>
            ) : null}
            <Button
              data-testid="login-submit"
              type="submit"
              disabled={busy}
              className="h-11 w-full border border-[var(--aurion-gold)] bg-[var(--aurion-gold)]/10 font-semibold tracking-widest text-[var(--aurion-gold)] hover:bg-[var(--aurion-gold)]/20"
            >
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Sign in
            </Button>
          </form>
          <div className="mt-6 text-center text-xs text-[var(--aurion-muted)]">
            Have an invite?{" "}
            <Link
              to="/register"
              data-testid="goto-register"
              className="text-[var(--aurion-gold)] hover:underline"
            >
              Redeem it here
            </Link>
          </div>
        </Card>
      </div>
    </div>
  );
}
